import type { CouponId, CouponIdentifiers, CouponType } from '../../../../schema/coupon/entry.js';
import { getInstanceClient } from '../../../di/entry.browser.js';
import { PoolbettingError } from '../../../domain/entry.js';
import { type ActionResult, groupOutcomesByCount } from '../../../lib/entry.browser.js';

type CreateCouponArgs = {
  couponId: CouponId;
};

/**
 * Create a new coupon for given coupon.
 *
 * @param couponType Coupon type to create
 * @param root0 Function arguments
 * @param root0.couponId Unique coupon id
 * @returns SUCCESS with payload if successful, otherwise ERROR
 */
export async function createCoupon(
  couponType: CouponType,
  { couponId }: CreateCouponArgs
): Promise<ActionResult<CouponIdentifiers>> {
  const getCouponsController = getInstanceClient('IGetCouponsController');
  const createCouponController = getInstanceClient('ICreateCouponController');
  const [getCoupons] = getCouponsController({ couponId });

  try {
    const [coupon] = getCoupons();

    if (!coupon?.boards[0]) {
      throw new Error('Unable to get coupon');
    }

    const outcomeGroups = groupOutcomesByCount(coupon.boards[0], coupon.selectedSystem);
    const eventCount = Object.values(outcomeGroups).reduce((sum, v) => v + sum, 0);

    // For now it is only valid to create a coupon for existing coupon collections.
    // We might move eventCount into the use case, but then we might want to utilize drawService and existing Query Client to get cached draws
    const response = await createCouponController({
      payload: {
        couponType,
        eventCount,
        drawNumber: coupon.drawNumber,
        itsProduct: coupon.product,
        betAmount: coupon.betAmount,
      },
    });

    return { status: 'SUCCESS', data: response };
  } catch (error) {
    if (PoolbettingError.isInstance(error)) {
      return error.toActionResult();
    }

    return { status: 'ERROR', error: 'Det gick inte att skapa en kupong' };
  }
}
